import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PerformanceOverviewCard from './components/PerformanceOverviewCard';
import GoalTrackingPanel from './components/GoalTrackingPanel';
import TrainingManagementPanel from './components/TrainingManagementPanel';
import SuccessionPlanningPanel from './components/SuccessionPlanningPanel';
import PerformanceReviewPanel from './components/PerformanceReviewPanel';
import DevelopmentAnalyticsPanel from './components/DevelopmentAnalyticsPanel';

const PerformanceStaffDevelopmentHub = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    department: 'all',
    reviewCycle: 'current',
    performanceLevel: 'all'
  });

  const [performanceMetrics, setPerformanceMetrics] = useState({
    departmentPerformanceScore: 0,
    trainingCompletionRate: 0,
    successionReadiness: 0,
    developmentBudgetUtilization: 0,
    activeGoals: 0,
    completedTrainings: 0,
    highPotentialEmployees: 0,
    criticalPositionsCovered: 0
  });

  const tabs = [
    { id: 'overview', label: 'Performance Overview', icon: 'BarChart3' },
    { id: 'goals', label: 'Goal Setting & Tracking', icon: 'Target' },
    { id: 'training', label: 'Training Management', icon: 'GraduationCap' },
    { id: 'succession', label: 'Succession Planning', icon: 'Users' },
    { id: 'reviews', label: 'Performance Reviews', icon: 'ClipboardCheck' },
    { id: 'analytics', label: 'Development Analytics', icon: 'TrendingUp' }
  ];

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
  };

  const handleExport = (format) => {
    setSuccess(`Exporting data in ${format} format...`);
    setTimeout(() => setSuccess(''), 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      <Header />
      <ComplianceAlertBanner />
      <LegalDisclaimerBanner />
      <GlobalFilterBar onFilterChange={handleFilterChange} />
      <DataRefreshIndicator />
      <main className="pt-32 pb-12 px-4 md:px-6 lg:px-8 max-w-[1600px] mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shadow-lg">
              <Icon name="TrendingUp" size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900">
                Performance & Staff Development Hub
              </h1>
              <p className="text-slate-600 mt-1">
                Comprehensive talent management with goal setting, training coordination, and succession planning
              </p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <Icon name="AlertCircle" className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
              <Icon name="X" size={18} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
            <Icon name="CheckCircle" className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-emerald-800 font-medium">{success}</p>
            </div>
            <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-600">
              <Icon name="X" size={18} />
            </button>
          </div>
        )}

        {/* Action Bar */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <select
              value={filters?.department}
              onChange={(e) => handleFilterChange({ department: e?.target?.value })}
              className="px-4 py-2 border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Departments</option>
              <option value="hr">Human Resources</option>
              <option value="finance">Finance</option>
              <option value="operations">Operations</option>
              <option value="it">Information Technology</option>
            </select>

            <select
              value={filters?.reviewCycle}
              onChange={(e) => handleFilterChange({ reviewCycle: e?.target?.value })}
              className="px-4 py-2 border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="current">Current Review Cycle</option>
              <option value="q1-2026">Q1 2026</option>
              <option value="q4-2025">Q4 2025</option>
              <option value="annual-2025">Annual 2025</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" iconName="Download" onClick={() => handleExport('PDF')}>
              Export Report
            </Button>
            <Button variant="primary" iconName="Plus">
              Create Development Plan
            </Button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6 bg-white rounded-xl shadow-sm border border-slate-200 p-2">
          <div className="flex flex-wrap gap-2">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  activeTab === tab?.id
                    ? 'bg-gradient-to-r from-blue-500 to-emerald-500 text-white shadow-md'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span className="text-sm">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Performance Overview Cards - 4 columns */}
              <div className="lg:col-span-4 space-y-6">
                <PerformanceOverviewCard metrics={performanceMetrics} />
              </div>

              {/* Main Management Panel - 8 columns */}
              <div className="lg:col-span-8 space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Icon name="Activity" size={24} className="text-blue-600" />
                    Performance Dashboard
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900">Active Goals</span>
                        <Icon name="Target" size={20} className="text-blue-600" />
                      </div>
                      <p className="text-3xl font-bold text-blue-900">{performanceMetrics?.activeGoals}</p>
                      <p className="text-xs text-blue-700 mt-1">Across all departments</p>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-emerald-900">Completed Trainings</span>
                        <Icon name="GraduationCap" size={20} className="text-emerald-600" />
                      </div>
                      <p className="text-3xl font-bold text-emerald-900">{performanceMetrics?.completedTrainings}</p>
                      <p className="text-xs text-emerald-700 mt-1">This quarter</p>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-purple-900">High-Potential Staff</span>
                        <Icon name="Star" size={20} className="text-purple-600" />
                      </div>
                      <p className="text-3xl font-bold text-purple-900">{performanceMetrics?.highPotentialEmployees}</p>
                      <p className="text-xs text-purple-700 mt-1">Identified for succession</p>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-amber-900">Critical Positions</span>
                        <Icon name="Shield" size={20} className="text-amber-600" />
                      </div>
                      <p className="text-3xl font-bold text-amber-900">{performanceMetrics?.criticalPositionsCovered}</p>
                      <p className="text-xs text-amber-700 mt-1">Succession plans ready</p>
                    </div>
                  </div>
                </div>

                <DevelopmentAnalyticsPanel filters={filters} />
              </div>
            </div>
          )}

          {activeTab === 'goals' && <GoalTrackingPanel filters={filters} />}
          {activeTab === 'training' && <TrainingManagementPanel filters={filters} />}
          {activeTab === 'succession' && <SuccessionPlanningPanel filters={filters} />}
          {activeTab === 'reviews' && <PerformanceReviewPanel filters={filters} />}
          {activeTab === 'analytics' && <DevelopmentAnalyticsPanel filters={filters} />}
        </div>
      </main>
    </div>
  );
};

export default PerformanceStaffDevelopmentHub;